// src/pages/MarketMap/CurveChart.jsx
import React from "react";
import SectionCard from "components/SectionCard";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

export default function CurveChart({
  title,
  data,
  series,
  visible,
  yFormatter,
  tooltipFormatter,
  checkboxes,
  height = "h-64",
  latestMap = {},
}) {
  // ----- compute Y-axis domain + ticks -----
  const vals = [];
  data.forEach((row) => {
    series.forEach((s) => {
      if (visible[s.key] && typeof row[s.key] === "number") vals.push(row[s.key]);
    });
  });

  let domain = [0, 10];
  let ticks = [0, 2, 4, 6, 8, 10];

  if (vals.length) {
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const stepCandidates = [0.25, 0.5, 1, 2];
    let step = 1;
    let minR = Math.floor(min);
    let maxR = Math.ceil(max);

    for (const s of stepCandidates) {
      const minTest = Math.floor(min / s) * s;
      const maxTest = Math.ceil(max / s) * s;
      const count = Math.round((maxTest - minTest) / s) + 1;
      if (count >= 4 && count <= 6) {
        step = s;
        minR = minTest;
        maxR = maxTest;
        break;
      }
    }

    const arr = [];
    for (let v = minR; v <= maxR + 1e-9; v += step) arr.push(+v.toFixed(2));
    domain = [minR, maxR];
    ticks = arr;
  }

  // ----- filter visible rows -----
  const filteredData = data.filter((row) =>
    series.some((s) => visible[s.key] && typeof row[s.key] === "number")
  );

  // ----- dynamic tenor x-axis ticks -----
  const activeTenors = new Set(filteredData.map((row) => row.tenor));
  const xTicks = Array.from(activeTenors).sort((a, b) => {
    const parse = (t) => {
      const m = t.match(/^(\d+)([my])$/i);
      if (!m) return 999;
      const n = parseInt(m[1], 10);
      return m[2].toLowerCase() === "m" ? n / 12 : n;
    };
    return parse(a) - parse(b);
  });

  const renderLegend = () => (
    <ul className="flex gap-4 text-sm" style={{ color: "hsl(var(--foreground))" }}>
      {series.map(
        (s) =>
          visible[s.key] && (
            <li key={s.key} className="flex items-center gap-1">
              <span
                style={{
                  display: "inline-block",
                  width: 20,
                  height: 3,
                  backgroundColor: s.color,
                }}
              />
              {latestMap[s.key]?.name || s.label}
            </li>
          )
      )}
    </ul>
  );

  return (
    <SectionCard title={title}>
      {checkboxes && <div className="text-xs text-muted mb-2">{checkboxes}</div>}
      <div className={height}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={filteredData} margin={{ left: 8, right: 8, top: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />

            {/* ---- X Axis with themed tick color ---- */}
            <XAxis
              dataKey="tenor"
              type="category"
              ticks={xTicks}
              allowDuplicatedCategory={false}
              tick={{ fill: "hsl(var(--muted-foreground))" }}
            />

            {/* ---- Y Axis with themed tick color ---- */}
            <YAxis
              domain={domain}
              ticks={ticks}
              tickFormatter={yFormatter}
              tick={{ fill: "hsl(var(--muted-foreground))" }}
            />

            <Tooltip
              formatter={tooltipFormatter}
              contentStyle={{
                backgroundColor: "hsl(var(--background))",
                border: "1px solid hsl(var(--border))",
                color: "hsl(var(--foreground))",
              }}
              labelStyle={{ color: "hsl(var(--foreground))" }}
            />

            <Legend content={renderLegend} />

            {series.map(
              (s) =>
                visible[s.key] && (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    name={s.label}
                    stroke={s.color}
                    strokeWidth={3}
                    dot={false}
                  />
                )
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </SectionCard>
  );
}
